import {Component, inject, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {
  faBars,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faCircleXmark,
  faClipboardCheck,
  faClipboardList
} from "@fortawesome/pro-solid-svg-icons";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {MatTableDataSource} from "@angular/material/table";
import {CircleState} from "../../../../util/design/circle-state/circle-state.component";
import {BIMBaseCheck, BimBaseCheckControllerService, IFCCheck, Task} from "../../../../../api";
import {TaskDetailsService} from "../../../../../services/tasks/task-details.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ExchangeService} from "../../../../../services/exchange/exchange.service";
import {BimService} from "../../../../../bim-api";
import {HttpClient} from "@angular/common/http";
import {DatePipe} from "@angular/common";
import {OverviewReportComponent} from "../../../overviewReport/overview-report.component";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable
} from "@angular/cdk/table";
import {BicButtonComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {StateConfigService} from "../../../../../services/state/state-config.service";
import {LoiCheckStepperComponent} from "../loi-check-stepper/loi-check-stepper.component";
import {BimViewerService, TopicCreationData} from "../../../../../services/bim-viewer/bim-viewer.service";
import {IfcCheckPrefetchService} from '../../../../../services/prefetch/ifc-check-prefetch.service';


export interface CheckRule {
  id: string,
  rule_name: string,
  rule_description: string,
  text: string,
  status: string,
  guid_fail_list: string[],
  guid_pass_list: string[],
}

@Component({
  selector: 'app-bim-base-check-reports',
  templateUrl: './bim-base-check-report.component.html',
  styleUrls: ['./bim-base-check-report.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    CdkCell,
    CdkHeaderCell,
    CdkColumnDef,
    CdkHeaderCellDef,
    CdkCellDef,
    CdkHeaderRow,
    CdkRow,
    CdkRowDef,
    CdkHeaderRowDef,
    BicButtonComponent,
    TooltipDirective,
    CdkTable,
    OverviewReportComponent,
    LoiCheckStepperComponent

  ]
})
export class BimBaseCheckReportComponent implements OnInit, OnChanges {
  private taskDetailService = inject(TaskDetailsService);
  private bimBaseCheckControllerService = inject(BimBaseCheckControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private exchangeService = inject(ExchangeService);
  private http = inject(HttpClient);
  private bimService = inject(BimService);
  private datePipe = inject(DatePipe);
  check: BIMBaseCheck = {};
  loading = true;
  checkRuleType?: IFCCheck.CheckRuleTypeEnum;
  private stateConfigService = inject(StateConfigService);
  private bimViewerService = inject(BimViewerService);
  private ifcCheckPrefetchService = inject(IfcCheckPrefetchService);

  displayedColumns: string[] = ['info', 'rule', 'result', 'state', 'task'];
  dataSource: MatTableDataSource<CheckRule> = new MatTableDataSource<CheckRule>();
  private router = inject(Router);

  protected readonly faBars = faBars;
  protected readonly CircleState = CircleState;
  protected readonly faClipboardList = faClipboardList;

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      if (params.reportId === undefined) {
        return;
      }
      this.loading = true;
      this.check = {id: params.reportId}
      this.loadCheckReport(params.reportId);
    })
  }

  private loadCheckReport(reportId: string): void {
    this.ifcCheckPrefetchService.getCheck(reportId).subscribe({
      next: (check) => {
        this.checkRuleType = check.checkRuleType;
        const bimBaseCheckFromIfc = check.bimBaseCheck;
        if (bimBaseCheckFromIfc?.results) {
          this.applyCheckData(bimBaseCheckFromIfc);
          return;
        }
        this.loadBimBaseCheck(reportId, bimBaseCheckFromIfc?.id);
      },
      error: () => {
        this.loadBimBaseCheck(reportId);
      }
    });
  }

  private loadBimBaseCheck(reportId: string, bimBaseCheckId?: string): void {
    this.bimBaseCheckControllerService.getChecks2(this.stateConfigService.getProjectId()).subscribe({
      next: (checks) => {
        const check = checks.find(c => c.id === bimBaseCheckId || c.id === reportId || c.checkId === reportId);
        this.applyCheckData(check);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private applyCheckData(check?: BIMBaseCheck): void {
    if (!check) {
      this.dataSource.data = [];
      this.loading = false;
      return;
    }

    this.check = check;
    this.dataSource.data = this.parseResults(check.results);
    this.loading = false;
  }

  private parseResults(results: unknown): CheckRule[] {
    if (Array.isArray(results)) {
      return results as CheckRule[];
    }
    if (!results || typeof results !== 'string') {
      return [];
    }
    try {
      const parsed = JSON.parse(results);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.result) {
      this.dataSource.data = changes.result.currentValue;
    }

    if (changes.check) {
      this.check = changes.check.currentValue;
      this.dataSource.data = this.parseResults(this.check?.results);
    }
  }

  get totalRules() {
    return this.dataSource.data.length;
  }

  totalCompleted() {
    let allCompleted = this.dataSource.data.filter(t => t.status === "success").length;
    return Math.round(allCompleted / this.totalRules * 100);
  }

  totalWarning() {
    let allWarning = this.dataSource.data.filter(t => t.status === "warning").length;
    return Math.round(allWarning / this.totalRules * 100);
  }

  totalError() {
    let allFailed = this.dataSource.data.filter(t => t.status === "error").length;
    return Math.round(allFailed / this.totalRules * 100);
  }

  createTask(rule: CheckRule) {
    let task = {
      markup: {
        topic: {
          title: 'Überarbeiten notwendig für: ' + rule.rule_name
        }
      },
    }
    this.taskDetailService.setOpenedTask(task as Task);
  }

  setGuids(row: CheckRule) {
    this.exchangeService.setSuccessGuids(row.guid_pass_list);
    this.exchangeService.setFailedGuids(row.guid_fail_list);
  }

  openPressed() {
    if (!this.check.projectFile) {
      console.error('BIM-Basis-Check: Kein projectFile am Check vorhanden - kann nicht im Viewer geöffnet werden.', this.check);
      return;
    }

    const topicInputs: TopicCreationData[] = []

    this.dataSource.data.forEach(rule => {
      // guid_fail_list kommt aus Backend-JSON und kann trotz Typ fehlen (siehe parseResults).
      const guidFailList = rule.guid_fail_list ?? [];
      if (guidFailList.length === 0) return;

      topicInputs.push({
        title: rule.rule_name,
        description: rule.rule_description + "\n\n" + rule.text,
        guids: [...guidFailList],
        labels: ['BIM-Basis-Check'],
        specification: rule.rule_name,
      });
    });

    this.bimViewerService.open(this.check.projectFile, topicInputs);
  }

  downloadReport() {
    const formattedDate = `${this.datePipe.transform(this.check.checkDate, 'dd.MM.y')} - ${this.datePipe.transform(this.check.checkDate, 'HH:mm')}`;
    let payload = {
      rules: this.dataSource.data,
      template_path: "https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/bericht_bim_basis_check_template.xlsx",
      target_texts: {
        "Prüfung durchgeführt am:": formattedDate,
        "Geprüfte Regeln:": this.totalRules,
        "Geprüftes Modell:": this.check?.projectFile?.name?.split('.').slice(0, -1).join('.') ?? '',
        "Gesamtergebnis:": this.totalCompleted() / 100
      }
    }

    this.http.post('https://services.thinkbic.de/bim/fill-bim-basis-template', payload,
      {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token',) || ''}`
        }
      }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bericht_bim_basis_check.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCircleXmark = faCircleXmark;


  protected readonly faQuestionCircle = faCircleQuestion;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faSpinnerThird = faSpinnerThird;

  get fileSize() {
    if (!this.check?.projectFile?.size) {
      return '';
    }
    const sizeInMB = this.check.projectFile.size / (1024 * 1024);
    return sizeInMB.toFixed(2) + ' MB';
  }

  get fileName() {
    return this.check?.projectFile?.name ?? '';
  }

  get checkerName() {
    if (!this.check?.checker?.firstName) return '';
    return this.check.checker.firstName + ' ' + this.check.checker.lastName;
  }

  navigateToDetailCheckReport() {
    const route = this.checkRuleType === 'IDS' ? 'ids-check' : 'loi-check';
    this.router.navigate([route], {relativeTo: this.activatedRoute.parent})
  }

  get detailCheckLabel(): string {
    if (!this.checkRuleType) return '';
    return this.checkRuleType === 'IDS' ? 'IDS-Check' : 'LOI-Check';
  }
}
